/**
 * AppLayout — QYAS-style app shell for qfinpricer-portal.
 *
 *   ┌─────────────────────────────────────────────────┐
 *   │ AppBar (48px) — logo + (optional) WorkspaceTabs │
 *   ├─────────────────────────────────────────────────┤
 *   │ SectionSubTabs (40px)  ← only inside a section  │
 *   ├───────────────┬─────────────────────────────────┤
 *   │               │ Breadcrumbs                     │
 *   │   Drawer      │ <Outlet />                      │
 *   │   (280px,     │                                 │
 *   │   collapsible)│                                 │
 *   │               │                                 │
 *   └───────────────┴─────────────────────────────────┘
 *
 * Mounted by router.tsx in place of PortalShell. Authenticated routes only.
 *
 * Day-1 ship: layout file in place but router.tsx is NOT yet swapped.
 * That cutover is Day-2 work — see spec acceptance criteria.
 *
 * Spec: 2026-05-19-qfinpricer-portal-skin-qyas.md (Day 1, item 1)
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import KeepAliveOutlet from './KeepAliveOutlet';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Menu as MenuIcon, ChevronLeft as ChevronLeftIcon } from 'lucide-react';
import { Navigation } from '../components/navigation/Navigation';
import { UserMenu } from '../components/navigation/UserMenu';
import { Breadcrumbs } from '../components/navigation/Breadcrumbs';
import { SectionSubTabs } from '../components/navigation/SectionSubTabs';
import { WorkspaceTabs } from '../components/navigation/WorkspaceTabs';
// DensityToggle + AppearanceToggle are no longer mounted in the AppBar.
// Both controls live in /settings as of 2026-05-24.
import {
  WorkspaceTabsProvider,
  isWorkspaceTabsEnabled,
} from '../components/navigation/workspaceTabsStore';

const DRAWER_WIDTH = 280;
const APPBAR_HEIGHT = 48;

const AppLayoutInner: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const tabsEnabled = isWorkspaceTabsEnabled();

  const handleDrawerToggle = () => setDrawerOpen((o) => !o);
  const handleNavigateInDrawer = () => {
    if (isMobile) setDrawerOpen(false);
  };

  // Publish drawer width as a CSS variable so full-bleed children
  // (e.g. orchestrator.module.css `position: fixed`) can offset past it
  // and keep the QFinPricer sidebar accessible.
  useEffect(() => {
    const px = drawerOpen && !isMobile ? `${DRAWER_WIDTH}px` : '0px';
    document.documentElement.style.setProperty('--qfp-drawer-px', px);
  }, [drawerOpen, isMobile]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar sx={{ minHeight: `${APPBAR_HEIGHT}px !important`, px: 2, gap: 1 }}>
          <IconButton
            edge="start"
            onClick={handleDrawerToggle}
            aria-label={drawerOpen ? 'Close navigation menu' : 'Open navigation menu'}
          >
            {drawerOpen ? <ChevronLeftIcon size={18} /> : <MenuIcon size={18} />}
          </IconButton>

          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: 0.5,
                bgcolor: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  color: 'primary.contrastText',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  fontFamily: '"JetBrains Mono", monospace',
                }}
              >
                QF
              </Typography>
            </Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: theme.palette.text.primary }}>
              QFinPricer
            </Typography>
          </Box>

          {/* Optional inline workspace-tabs bar — opt-in via VITE_WORKSPACE_TABS=true */}
          {tabsEnabled && (
            <Box sx={{ flex: 1, mx: 2, overflow: 'hidden' }}>
              <WorkspaceTabs />
            </Box>
          )}

          {!tabsEnabled && <Box sx={{ flexGrow: 1 }} />}

          <UserMenu />
        </Toolbar>
      </AppBar>

      {/* Navigation Drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={drawerOpen}
        onClose={isMobile ? handleDrawerToggle : undefined}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            top: APPBAR_HEIGHT,
            height: `calc(100% - ${APPBAR_HEIGHT}px)`,
          },
        }}
      >
        <Navigation onNavigate={handleNavigateInDrawer} />
      </Drawer>

      {/* Main column */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0, // prevent flex overflow from long tables
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          marginLeft: drawerOpen && !isMobile ? 0 : `-${DRAWER_WIDTH}px`,
          mt: `${APPBAR_HEIGHT}px`,
        }}
      >
        {/* Section sub-tabs (only renders when inside a registered section) */}
        <SectionSubTabs />

        <Box sx={{ p: 3 }}>
          <Breadcrumbs />
          <KeepAliveOutlet />
        </Box>
      </Box>
    </Box>
  );
};

export const AppLayout: React.FC = () => (
  <WorkspaceTabsProvider>
    <AppLayoutInner />
  </WorkspaceTabsProvider>
);

export default AppLayout;
